import { MenuItem } from '@atsystems/ui';
import { MenuConfig } from '../models/user-settings.model';

export interface AppliedMenu {
  menu: MenuItem[];
  favorites: MenuItem[];
  favoriteIds: string[];
}

export function applyUserConfig(
  base: MenuItem[],
  config: MenuConfig | null,
  canView: (item: MenuItem) => boolean,
): AppliedMenu {
  const order = config?.order ?? [];
  const favoriteIds = config?.favorites ?? [];

  const menu = reorderTopLevel(base, order);
  const favorites = collectFavorites(base, favoriteIds, canView);

  return { menu, favorites, favoriteIds: favorites.map(f => f.id) };
}

function reorderTopLevel(base: MenuItem[], order: string[]): MenuItem[] {
  if (!order.length) return [...base];

  const byId = new Map(base.map(item => [item.id, item]));
  const ordered = order
    .map(id => byId.get(id))
    .filter((item): item is MenuItem => !!item);
  const orderedIds = new Set(ordered.map(item => item.id));

  const result = [...ordered];
  base.forEach((item, defaultIndex) => {
    if (orderedIds.has(item.id)) return;
    let insertAt = result.length;
    for (let i = 0; i < result.length; i++) {
      if (base.indexOf(result[i]) > defaultIndex) {
        insertAt = i;
        break;
      }
    }
    result.splice(insertAt, 0, item);
  });

  return result;
}

function flattenWithParent(items: MenuItem[], parentLabel?: string): MenuItem[] {
  const acc: MenuItem[] = [];
  for (const item of items) {
    if (item.route || item.command) acc.push({ ...item, parentLabel });
    if (item.items?.length) acc.push(...flattenWithParent(item.items, item.label));
  }
  return acc;
}

function collectFavorites(
  base: MenuItem[],
  favoriteIds: string[],
  canView: (item: MenuItem) => boolean,
): MenuItem[] {
  const byId = new Map(flattenWithParent(base).map(item => [item.id, item]));
  return favoriteIds
    .map(id => byId.get(id))
    .filter((item): item is MenuItem => !!item && canView(item));
}
